import { useQuery } from 'react-query';
import { useDebounceValue } from 'usehooks-ts';
import { type IMapLayerSource } from '../layers/MapLayer.ts';
import {
  fetchLayerMetadata,
  getLayerMetadataCacheKey,
  isIntrospectableLayerUrl,
  isLayerMetadataConfigured,
  isMetadataSupportedLayerType,
} from '../metadata/fetchLayerMetadata.ts';
import { type LayerMetadataResult } from '../metadata/LayerMetadata.ts';

export type LayerMetadataStatus =
  | 'idle'
  | 'loading'
  | LayerMetadataResult['status'];

const METADATA_STALE_TIME = 10 * 60 * 1000;
const METADATA_CACHE_TIME = 30 * 60 * 1000;

/**
 * Introspezione del map service: restituisce gli attributi disponibili
 * per la fonte dati di un layer, con caching per (tipo, URL normalizzato,
 * opzioni protocollo).
 *
 * Per i tipi non supportati, gli URL non validi o le config incomplete
 * per il protocollo (es. layer WFS senza typeName) non viene effettuata
 * alcuna richiesta (`status: 'idle' | 'unsupported-type'`) e gli input
 * attributi restano in modalità manuale.
 */
export function useMapLayerAttributes(source: IMapLayerSource) {
  const trimmedUrl = source.url?.trim() ?? '';
  // L'URL completo (query string inclusa) viene passato al resolver:
  // può contenere parametri necessari al servizio (es. `token`).
  const [debouncedUrl] = useDebounceValue(trimmedUrl, 300);
  const debouncedSource: IMapLayerSource = { ...source, url: debouncedUrl };

  const supported = isMetadataSupportedLayerType(source.type);
  const enabled =
    supported &&
    isIntrospectableLayerUrl(debouncedUrl) &&
    isLayerMetadataConfigured(debouncedSource);

  const query = useQuery(
    getLayerMetadataCacheKey(debouncedSource),
    () => fetchLayerMetadata(debouncedSource),
    {
      enabled,
      staleTime: METADATA_STALE_TIME,
      cacheTime: METADATA_CACHE_TIME,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const result = query.data;
  const metadata = result?.status === 'ok' ? result.metadata : undefined;

  let status: LayerMetadataStatus;
  if (!supported) {
    status = 'unsupported-type';
  } else if (!enabled) {
    // L'URL digitato non è (ancora) introspezionabile, mancano opzioni
    // protocollo (es. typeName WFS), oppure il debounce non ha ancora
    // raggiunto un URL valido.
    status = 'idle';
  } else if (query.isLoading || debouncedUrl !== trimmedUrl) {
    status = 'loading';
  } else {
    status = result?.status ?? 'loading';
  }

  return {
    availableAttributes: metadata?.attributes ?? [],
    geometryType: metadata?.geometryType,
    isLoading: status === 'loading',
    isError:
      status === 'service-root' ||
      status === 'unreachable' ||
      status === 'invalid-response',
    status,
  };
}
